import React, { useState, useEffect, useReducer, useContext, useMemo, useRef } from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme as useThemeV4 } from "@material-ui/core/styles";
import {
  createTheme as createThemeV5,
  ThemeProvider as ThemeProviderV5,
} from "@mui/material/styles";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import {
  Avatar,
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  Plus,
  Pencil,
  Trash2,
  Tag as TagIcon,
  Users,
  Inbox,
  MoreHorizontal,
} from "lucide-react";

import api from "../../services/api";
import HubPageHeader from "../../components/HubPageHeader";
import MainContainer from "../../components/MainContainer";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TagModal from "../../components/TagModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ContactTagListModal from "../../components/ContactTagListModal";
import { useSearch } from "../../context/Search/SearchContext";

const tLang = (key, fallback) => {
  const v = i18n.t(key);
  return !v || v === key ? fallback : v;
};

const hexToRgba = (hex, alpha = 1) => {
  if (!hex || typeof hex !== "string") return `rgba(25,118,210,${alpha})`;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return `rgba(25,118,210,${alpha})`;
  return `rgba(${r},${g},${b},${alpha})`;
};

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_TAGS":
      return [...state, ...action.payload];
    case "UPDATE_TAGS": {
      const tag = action.payload;
      const idx = state.findIndex((s) => s.id === tag.id);
      if (idx !== -1) {
        state[idx] = tag;
        return [...state];
      }
      return [tag, ...state];
    }
    case "DELETE_TAGS":
      return state.filter((tag) => tag.id !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const useTableStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.palette.type === "dark" || theme.mode === "dark";
  return {
    excelTable: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
      "& .MuiTableCell-root": {
        borderBottom: `1px solid ${dsColors.borderLight || (isDark ? "#1a1f29" : "#f0f0f0")}`,
        borderRight: "none",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
        fontSize: 13.5,
        padding: "14px 16px",
      },
      "& .MuiTableCell-head": {
        background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        backgroundColor: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        fontWeight: 700,
        fontSize: 11.5,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        borderBottom: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
        padding: "12px 16px",
        whiteSpace: "nowrap",
        position: "sticky",
        top: 0,
        zIndex: 3,
      },
      "& .MuiTableCell-head:first-child": {
        borderTopLeftRadius: ds.radius?.md ?? 12,
      },
      "& .MuiTableCell-head:last-child": {
        borderTopRightRadius: ds.radius?.md ?? 12,
      },
      "& tbody tr:last-child .MuiTableCell-root": {
        borderBottom: "none",
      },
    },
    tableScroll: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      maxHeight: "100%",
      overflowX: "auto",
      overflowY: "auto",
      ...theme.scrollbarStyles,
    },
    zebraRow: {
      transition: "background-color 0.18s ease",
      "&:hover": {
        backgroundColor: dsColors.surfaceHover || (isDark ? "#171b24" : "#f3f4f6"),
      },
    },
    actionsCell: {
      whiteSpace: "nowrap",
      paddingRight: "16px !important",
      "& > *": { marginLeft: 4 },
      "& > *:first-child": { marginLeft: 0 },
    },
    iconActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      },
    },
    deleteActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: dsColors.danger || "#e53935",
      "&:hover": {
        background: dsColors.dangerBg || "rgba(229,57,53,0.10)",
        color: dsColors.dangerHover || "#c62828",
      },
    },
  };
});

const KpiPill = ({ icon, label, value, accent, isDark }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.25}
    sx={{
      px: 1.75,
      py: 1.25,
      borderRadius: 2.5,
      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
      border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
      flex: 1,
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        width: 32, height: 32, borderRadius: 1.5,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: `linear-gradient(135deg, ${accent} 0%, ${hexToRgba(accent, 0.7)} 100%)`,
        color: "#fff",
        boxShadow: `0 4px 12px ${hexToRgba(accent, 0.3)}`,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "text.secondary", lineHeight: 1.2 }} noWrap>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: "text.primary", letterSpacing: -0.3, lineHeight: 1.2 }} noWrap>
        {value}
      </Typography>
    </Box>
  </Stack>
);

const TagPill = ({ tag, isDark }) => {
  const color = tag.color || (isDark ? "#475569" : "#94a3b8");
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        color,
        background: hexToRgba(color, 0.18),
        border: `1px solid ${hexToRgba(color, 0.4)}`,
      }}
    >
      <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
      {tag.name}
    </Box>
  );
};

const Tags = () => {
  const tableClasses = useTableStyles();
  const themeV4 = useThemeV4();
  const isMobile = useMediaQuery(themeV4.breakpoints.down("sm"));
  const { user, socket } = useContext(AuthContext);
  const { ticketSearchParam: searchParam, setTicketSearchParam, clear: clearSearch } = useSearch();

  const isDark = (themeV4?.palette?.type || themeV4?.mode) === "dark";
  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";
  const PRIMARY_CONTRAST = themeV4?.palette?.primary?.contrastText || "#fff";

  const tokens = useMemo(
    () => ({
      cardBg: isDark ? "#11141b" : "#ffffff",
      border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
      shadow: isDark
        ? "0 1px 0 rgba(255,255,255,0.02) inset, 0 8px 24px rgba(0,0,0,0.45)"
        : "0 1px 0 rgba(255,255,255,0.6) inset, 0 8px 24px rgba(15,23,42,0.06)",
      textPrimary: isDark ? "#e9edef" : "#0f172a",
      textSecondary: isDark ? "#8696a0" : "#64748b",
    }),
    [isDark]
  );

  const v5Theme = useMemo(
    () =>
      createThemeV5({
        palette: {
          mode: isDark ? "dark" : "light",
          primary: { main: PRIMARY_MAIN, dark: PRIMARY_DARK, contrastText: PRIMARY_CONTRAST },
          background: {
            default: isDark ? "#121212" : "#ffffff",
            paper: isDark ? "#0b0d12" : "#ffffff",
          },
          divider: isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)",
        },
        components: {
          MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 12 } } },
          MuiButton: { styleOverrides: { root: { borderRadius: 12 } } },
        },
      }),
    [isDark, PRIMARY_MAIN, PRIMARY_DARK, PRIMARY_CONTRAST]
  );

  const [selectedTagContacts, setSelectedTagContacts] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedTagName, setSelectedTagName] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [tags, dispatch] = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const loadingMoreRef = useRef(false);

  // Limpa busca global ao sair
  useEffect(() => () => clearSearch(), [clearSearch]);

  // Reseta lista quando muda o termo de busca
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    const fetchMoreTags = async () => {
      try {
        const { data } = await api.get("/tags/", {
          params: { searchParam, pageNumber, kanban: 0 },
        });
        dispatch({ type: "LOAD_TAGS", payload: data.tags });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
        loadingMoreRef.current = false;
      }
    };
    if (pageNumber > 0) {
      setLoading(true);
      const t = setTimeout(fetchMoreTags, 300);
      return () => clearTimeout(t);
    }
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const onCompanyTags = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_TAGS", payload: data.tag });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_TAGS", payload: +data.tagId });
      }
    };
    socket.on(`company${user.companyId}-tag`, onCompanyTags);
    return () => {
      socket.off(`company${user.companyId}-tag`, onCompanyTags);
    };
  }, [socket, user.companyId]);

  const handleOpenTagModal = () => { setSelectedTag(null); setTagModalOpen(true); };
  const handleCloseTagModal = () => { setSelectedTag(null); setTagModalOpen(false); };

  const handleEditTag = (tag) => { setSelectedTag(tag); setTagModalOpen(true); };

  const handleShowContacts = (contacts, tag) => {
    setSelectedTagContacts(contacts);
    setContactModalOpen(true);
    setSelectedTagName(tag);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
    setSelectedTagContacts([]);
    setSelectedTagName("");
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success(i18n.t("tags.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingTag(null);
    setTicketSearchParam("");
    setPageNumber(1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading || loadingMoreRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight <= 100) {
      loadingMoreRef.current = true;
      setPageNumber((p) => p + 1);
    }
  };

  const stats = useMemo(() => {
    const total = tags.length;
    const totalContacts = tags.reduce((acc, t) => acc + (t.contacts?.length || 0), 0);
    const withContacts = tags.filter((t) => (t.contacts?.length || 0) > 0).length;
    return { total, totalContacts, withContacts };
  }, [tags]);

  return (
    <ThemeProviderV5 theme={v5Theme}>
      <MainContainer>
        <Box
          sx={{
            background: isDark ? "#121212" : "#ffffff",
            height: "100%",
            width: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            paddingRight: "12px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {contactModalOpen && (
            <ContactTagListModal
              open={contactModalOpen}
              onClose={handleCloseContactModal}
              tag={selectedTagName}
            />
          )}
          <ConfirmationModal
            title={deletingTag && `${i18n.t("tags.confirmationModal.deleteTitle")}`}
            open={confirmModalOpen}
            onClose={() => setConfirmModalOpen(false)}
            onConfirm={() => handleDeleteTag(deletingTag.id)}
          >
            {i18n.t("tags.confirmationModal.deleteMessage")}
          </ConfirmationModal>
          <TagModal
            open={tagModalOpen}
            onClose={handleCloseTagModal}
            aria-labelledby="form-dialog-title"
            tagId={selectedTag && selectedTag.id}
            kanban={0}
          />

          <HubPageHeader
            icon={<TagIcon size={20} strokeWidth={1.8} />}
            title={tLang("tags.title", "Tags")}
            subtitle={tLang("tags.subtitle", "Organize seus contatos com etiquetas coloridas")}
            action={
              <Button
                onClick={handleOpenTagModal}
                variant="contained"
                startIcon={<Plus size={16} />}
                sx={{
                  background: `linear-gradient(135deg, ${PRIMARY_MAIN} 0%, ${PRIMARY_DARK} 100%)`,
                  color: PRIMARY_CONTRAST,
                  borderRadius: "12px",
                  px: 2.5,
                  py: 1,
                  fontWeight: 700,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  boxShadow: `0 6px 16px ${hexToRgba(PRIMARY_MAIN, 0.35)}`,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY_MAIN} 100%)`,
                    transform: "translateY(-1px)",
                    boxShadow: `0 10px 22px ${hexToRgba(PRIMARY_MAIN, 0.45)}`,
                  },
                }}
              >
                {i18n.t("tags.buttons.add")}
              </Button>
            }
          />

          {/* KPIs */}
          <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={4}>
              <KpiPill icon={<TagIcon size={18} />} label={tLang("tags.kpis.total", "Total de tags")} value={stats.total} accent={PRIMARY_MAIN} isDark={isDark} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <KpiPill icon={<Users size={18} />} label={tLang("tags.kpis.totalContacts", "Contatos taggeados")} value={stats.totalContacts} accent="#2EA85A" isDark={isDark} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <KpiPill icon={<Inbox size={18} />} label={tLang("tags.kpis.withContacts", "Tags em uso")} value={stats.withContacts} accent="#F7C82C" isDark={isDark} />
            </Grid>
          </Grid>

          {/* TABELA / CARDS */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              borderRadius: 3,
              background: tokens.cardBg,
              border: tokens.border,
              boxShadow: tokens.shadow,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {isMobile ? (
              <Box
                onScroll={handleScroll}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  ...(themeV4.scrollbarStyles || {}),
                }}
              >
                {tags.length === 0 && !loading ? (
                  <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ py: 6, px: 2 }}>
                    <Avatar sx={{ width: 56, height: 56, bgcolor: hexToRgba(PRIMARY_MAIN, isDark ? 0.15 : 0.10), color: PRIMARY_MAIN }}>
                      <TagIcon size={28} />
                    </Avatar>
                    <Typography sx={{ fontSize: 13, color: tokens.textSecondary, textAlign: "center" }}>
                      {tLang("tags.empty", "Nenhuma tag cadastrada.")}
                    </Typography>
                  </Stack>
                ) : (
                  tags.map((tag) => (
                    <Stack
                      key={tag.id}
                      direction="row"
                      alignItems="center"
                      spacing={1.25}
                      sx={{
                        px: 1.75,
                        py: 1.5,
                        borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)"}`,
                        "&:last-child": { borderBottom: "none" },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <TagPill tag={tag} isDark={isDark} />
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: tokens.textSecondary,
                            mt: 0.5,
                            cursor: tag.contacts?.length > 0 ? "pointer" : "default",
                            textDecoration: tag.contacts?.length > 0 ? "underline" : "none",
                          }}
                          onClick={() => tag.contacts?.length > 0 && handleShowContacts(tag.contacts, tag)}
                        >
                          {tag.contacts?.length || 0} {tLang("tags.table.contacts", "contatos")}
                        </Typography>
                      </Box>
                      <IconButton size="small" className={tableClasses.iconActionBtn} onClick={() => handleEditTag(tag)}>
                        <Pencil size={16} strokeWidth={2} />
                      </IconButton>
                      <IconButton
                        size="small"
                        className={tableClasses.deleteActionBtn}
                        onClick={() => { setConfirmModalOpen(true); setDeletingTag(tag); }}
                      >
                        <Trash2 size={16} strokeWidth={2} />
                      </IconButton>
                    </Stack>
                  ))
                )}
                {loading && <TableRowSkeleton columns={2} />}
              </Box>
            ) : (
              <TableContainer
                onScroll={handleScroll}
                className={tableClasses.tableScroll}
                style={{
                  flex: 1,
                  minHeight: 0,
                  height: "100%",
                  maxHeight: "100%",
                  overflow: "auto",
                }}
              >
                <Table stickyHeader className={tableClasses.excelTable} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: 80 }}>{tLang("tags.table.id", "ID")}</TableCell>
                      <TableCell>{tLang("tags.table.name", "Nome")}</TableCell>
                      <TableCell>{tLang("tags.table.contacts", "Contatos")}</TableCell>
                      <TableCell align="right" className={tableClasses.actionsCell} style={{ width: 140 }}>
                        {tLang("tags.table.actions", "Ações")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tags.length === 0 && !loading ? (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: "center", py: 6, color: tokens.textSecondary, fontStyle: "italic" }}>
                          {tLang("tags.empty", "Nenhuma tag cadastrada.")}
                        </TableCell>
                      </TableRow>
                    ) : (
                      tags.map((tag) => {
                        const contactCount = tag.contacts?.length || 0;
                        return (
                          <TableRow key={tag.id} className={tableClasses.zebraRow}>
                            <TableCell sx={{ color: tokens.textSecondary, fontSize: 12, fontWeight: 600 }}>
                              #{tag.id}
                            </TableCell>
                            <TableCell>
                              <TagPill tag={tag} isDark={isDark} />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: contactCount > 0 ? tokens.textPrimary : tokens.textSecondary,
                                  }}
                                >
                                  {contactCount} {contactCount === 1 ? tLang("tags.table.contact", "contato") : tLang("tags.table.contacts", "contatos")}
                                </Box>
                                {contactCount > 0 && (
                                  <Tooltip title={tLang("tags.tooltips.viewContacts", "Ver contatos")}>
                                    <IconButton
                                      size="small"
                                      className={tableClasses.iconActionBtn}
                                      onClick={() => handleShowContacts(tag.contacts, tag)}
                                    >
                                      <MoreHorizontal size={14} strokeWidth={2} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="right" className={tableClasses.actionsCell}>
                              <Tooltip title={tLang("tags.tooltips.edit", "Editar")}>
                                <IconButton
                                  size="small"
                                  className={tableClasses.iconActionBtn}
                                  onClick={() => handleEditTag(tag)}
                                >
                                  <Pencil size={16} strokeWidth={2} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={tLang("tags.tooltips.delete", "Excluir")}>
                                <IconButton
                                  size="small"
                                  className={tableClasses.deleteActionBtn}
                                  onClick={() => { setConfirmModalOpen(true); setDeletingTag(tag); }}
                                >
                                  <Trash2 size={16} strokeWidth={2} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                    {loading && <TableRowSkeleton columns={4} />}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
        </Box>
      </MainContainer>
    </ThemeProviderV5>
  );
};

export default Tags;
